import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Link from '~/components/Link';
import Select from '~/components/Form/Select';
import Form from '~/components/Form';
import Input from '~/components/Form/Input';
import routesPath from '~/constants/routes';
import Captcha from '~/components/Form/Captcha';
import * as S from '~/components/Form/styles';
import GridCell from '~/components/GridCell';
import DefaultLayout from '~/pages/_layouts/DefaultLayout';
import { getMediaTypes } from '~/services/api';
import { theme } from '~/styles/theme';
import { PopUpView } from '~/components';
import { PrivacyPolicy } from '~/static/privacyPolicy';
import { DataProtection } from '~/static/dataProtection';
import FoldedMenu from '~/components/FoldedMenu';
import { languageOptions } from '~/utils/languageOptions';
import LegalTermsContent from './components/LegalTermsContent';
import FormConfig from './components/FormConfig';

export default function Register() {
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [selectedOption, setSelectedOption] = useState(null);
  const [mediaTypeError, setMediaTypeError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [showModalPolicy, setModalPolicyVisibility] = useState(false);
  const [showModalData, setModalDataVisibility] = useState(false);

  const { loading } = useSelector(state => state.auth);
  const [mediaTypes, setMediaTypes] = useState([
    { label: t('other'), value: 'other' },
  ]);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('register');
    setCaptchaValue(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const checkValidations = () => {
    handleReCaptchaVerify();

    if (selectedOption !== {} && captchaValue !== null) {
      setMediaTypeError('');
      setCaptchaError('');
    } else {
      if (
        selectedOption === null ||
        Object.keys(selectedOption).length === 0 ||
        selectedOption.value === 'other'
      ) {
        setMediaTypeError(t('mediaTypeIsRequiredValidationError'));
      }
      if (captchaValue === null) {
        setCaptchaError(t('captchaIsRequiredValidationError'));
      }
    }
  };

  const mediaTypeFetchTrigger = useCallback(() => {
    const fetchMediaTypes = async () => {
      const otherOption = { label: t('other'), value: 'other' };
      try {
        const { data } = await getMediaTypes();
        setMediaTypes([
          ...data['hydra:member'].map(opt => ({
            label: opt.name,
            value: opt.name,
          })),
          otherOption,
        ]);
      } catch (err) {
        setMediaTypes([otherOption]);
      }
    };
    fetchMediaTypes();
  }, [t, setMediaTypes]);

  useEffect(() => {
    mediaTypeFetchTrigger();
  }, [mediaTypeFetchTrigger]);

  const areas = {
    xs:
      '"HEADER" "NAME" "SURNAME" "EMAIL" "PASSWORD" "PASSWORD2" "DNI" "MOBILE" "MEDIANAME" "MEDIATYPE" "MEDIATYPE2" "CHECK" "POLICY" "CAPTCHA" "SUBMIT" "ADVISE"',
    lg:
      '"HEADER HEADER" "NAME DNI" "SURNAME MOBILE" "EMAIL MEDIANAME" "PASSWORD MEDIATYPE" "PASSWORD2 MEDIATYPE2" "CHECK CHECK" "POLICY POLICY" "CAPTCHA CAPTCHA" "SUBMIT SUBMIT" "ADVISE ADVISE"',
  };

  const handleMediaTypeChange = option => {
    setSelectedOption(option);
  };

  return (
    <DefaultLayout>
      <FormConfig
        selectedOption={selectedOption}
        setMediaTypeError={setMediaTypeError}
        captchaValue={captchaValue}
        setCaptchaValue={setCaptchaValue}
        setCaptchaError={setCaptchaError}
      >
        {props => {
          // eslint-disable-next-line react/prop-types
          const { isSubmitting, handleSubmit, errors } = props;
          return (
            <S.FormContainer width={50} height="auto">
              <Form
                hasWidth={100}
                onSubmit={handleSubmit}
                columns={2}
                areas={areas}
                hasHeight="auto"
                register
              >
                <GridCell area="HEADER" centered>
                  <S.FormHeader>
                    <S.Logo />
                    <S.FormTitle>{t('createAccount')}</S.FormTitle>
                  </S.FormHeader>
                </GridCell>

                <GridCell area="NAME">
                  <Input
                    icon="avatar"
                    type="text"
                    selector="firstName"
                    label={t('name')}
                    placeholder={t('enterName')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="SURNAME">
                  <Input
                    icon="avatar"
                    type="text"
                    selector="lastName"
                    label={t('lastName')}
                    placeholder={t('enterLastName')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="DNI">
                  <Input
                    icon="idCard"
                    type="text"
                    selector="document"
                    label={t('dni')}
                    placeholder={t('enterDNI')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="MOBILE">
                  <Input
                    icon="phonePortrait"
                    type="tel"
                    selector="phoneNumber"
                    label={t('telephone')}
                    placeholder={t('enterTelephone')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="EMAIL">
                  <Input
                    icon="mail"
                    type="email"
                    selector="email"
                    label={t('email')}
                    placeholder={t('enterEmail')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="PASSWORD">
                  <Input
                    icon="lock"
                    type="password"
                    selector="plainPassword"
                    label={t('password')}
                    placeholder={t('password')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="PASSWORD2">
                  <Input
                    icon="lock"
                    type="password"
                    selector="passwordConfirmation"
                    label={t('confirmPasswordLabel')}
                    placeholder={t('confirmPassword')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="MEDIANAME">
                  <Input
                    icon="idCard"
                    type="text"
                    selector="mediaName"
                    label={t('mediaNameLabel')}
                    placeholder={t('enterMediaName')}
                    formikProps={props}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="MEDIATYPE">
                  <Select
                    icon="idCard"
                    selector="mediaType"
                    label={t('selectMediaType')}
                    value={selectedOption}
                    onChange={handleMediaTypeChange}
                    options={mediaTypes}
                    error={selectedOption ? '' : mediaTypeError}
                    placeholder={t('mediaType')}
                    disabled={loading}
                  />
                </GridCell>

                <GridCell area="MEDIATYPE2" centered>
                  {selectedOption !== null && selectedOption.value === 'other' && (
                    <Input
                      icon="idCard"
                      type="text"
                      selector="mediaType"
                      label={t('mediaType')}
                      placeholder={t('enterMediaTypeDescription')}
                      formikProps={{
                        ...props,
                        errors: {
                          ...errors,
                          mediaType: mediaTypeError,
                        },
                      }}
                      flashyEntrance
                      disabled={loading}
                    />
                  )}
                </GridCell>

                <GridCell area="CHECK" centered>
                  <Input
                    type="checkbox"
                    selector="profile"
                    text={t('profileConditions')}
                    formikProps={props}
                    disabled={loading}
                  />
                  <S.checkboxText htmlFor="profile">
                    {t('profileConditions')}
                  </S.checkboxText>
                </GridCell>
                <GridCell area="POLICY" centered>
                  <Input
                    type="checkbox"
                    selector="enabledConsent"
                    formikProps={props}
                    disabled={loading}
                  />
                  <S.checkboxText htmlFor="policy">
                    {t('legalText1')}{' '}
                    <span
                      role="button"
                      className="link"
                      tabIndex={0}
                      onKeyDown={() => setModalDataVisibility(true)}
                      onClick={e => {
                        e.preventDefault();
                        setModalDataVisibility(true);
                      }}
                    >
                      {t('legalTerms')}
                    </span>{' '}
                    {t('and')}{' '}
                    <span
                      role="button"
                      className="link"
                      tabIndex={0}
                      onKeyDown={() => setModalPolicyVisibility(true)}
                      onClick={e => {
                        e.preventDefault();
                        setModalPolicyVisibility(true);
                      }}
                    >
                      {t('privacyPolicy')}
                    </span>{' '}
                    {t('legalText2')}
                  </S.checkboxText>
                </GridCell>

                <GridCell area="CAPTCHA">
                  <Captcha
                    onVerify={setCaptchaValue}
                    captchaError={captchaError}
                  />
                </GridCell>

                <GridCell area="SUBMIT" centered>
                  <S.SubmitButton
                    type="submit"
                    onClick={() => checkValidations()}
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading
                      ? t('loading')
                      : t('createAccount')}
                  </S.SubmitButton>
                </GridCell>
                <GridCell area="ADVISE">
                  <S.Advice>{t('profesionalAdvise')}</S.Advice>
                </GridCell>
              </Form>
              <S.LoginZone>
                <p>{t('haveAccount')}</p>
                <div>
                  <Link to={routesPath.login}>{t('login')}</Link>
                </div>
              </S.LoginZone>
            </S.FormContainer>
          );
        }}
      </FormConfig>
      <PopUpView
        isOpen={showModalPolicy}
        onClose={() => {
          setModalPolicyVisibility(false);
        }}
        zone="whole"
        fullWidth={window.innerWidth <= theme.breakpoints.sm}
      >
        <LegalTermsContent
          data={PrivacyPolicy}
          closeModal={() => setModalPolicyVisibility(false)}
        />
      </PopUpView>
      <PopUpView
        isOpen={showModalData}
        onClose={() => setModalDataVisibility(false)}
        zone="whole"
        fullWidth={window.innerWidth <= theme.breakpoints.sm}
      >
        <LegalTermsContent
          data={DataProtection}
          closeModal={() => setModalDataVisibility(false)}
        />
      </PopUpView>
      <FoldedMenu invert label={t('languageId')} options={languageOptions()} />
    </DefaultLayout>
  );
}
